import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const INDEX_VERSION = 2;
let cachedIndex = null;
let cachePromise = null;
let cachedVersion = 0;

/**
 * Build searchable artist list from all artist profiles (full directory).
 */
export async function fetchArtistSearchIndex({ force = false } = {}) {
  if (!force && cachedIndex && cachedVersion === INDEX_VERSION) return cachedIndex;
  if (!force && cachePromise) return cachePromise;

  cachePromise = (async () => {
    const profilesSnap = await getDocs(collection(db, 'artistProfiles'));

    const index = profilesSnap.docs
      .map((docSnap) => {
        const data = docSnap.data() || {};
        const uid = data.uid || docSnap.id;
        if (!uid) return null;

        const name = String(data.name || '').trim();
        const nickname = String(data.nickname || '').trim();
        const displayName = name || nickname || 'Artist';
        const bio = String(data.bio || '').trim();
        const styles = Array.isArray(data.styles)
          ? data.styles.map((s) => String(s).trim()).filter(Boolean)
          : [];

        return {
          uid,
          displayName,
          name,
          nickname,
          bio,
          styles,
          profilePicture: data.profilePicture || null,
          avatarId: data.avatarId ?? null,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    cachedIndex = index;
    cachedVersion = INDEX_VERSION;
    return index;
  })();

  try {
    return await cachePromise;
  } finally {
    cachePromise = null;
  }
}

function artistNameFields(artist) {
  return [artist.name, artist.nickname, artist.displayName]
    .map((s) => String(s || '').trim().toLowerCase())
    .filter(Boolean);
}

/** Score how well an artist name matches the query (substring + prefix). */
function scoreArtistNameMatch(artist, q) {
  const fields = artistNameFields(artist);
  let best = 0;

  for (const field of fields) {
    if (field === q) best = Math.max(best, 5);
    else if (field.startsWith(q)) best = Math.max(best, 4);
    else if (field.includes(q)) best = Math.max(best, 3);

    for (const word of field.split(/[\s._-]+/)) {
      if (!word) continue;
      if (word === q) best = Math.max(best, 4);
      else if (word.startsWith(q)) best = Math.max(best, 3);
      else if (word.includes(q)) best = Math.max(best, 2);
    }
  }

  return best;
}

export function filterArtistSearchIndex(artists, queryText, limit = 12) {
  const q = String(queryText || '').trim().toLowerCase();
  if (!q || !Array.isArray(artists)) return [];

  const ranked = artists
    .map((artist) => {
      let score = scoreArtistNameMatch(artist, q);

      // Bio / styles only for 3+ characters (keeps "p" from matching "paint" in bios)
      if (score === 0 && q.length >= 3) {
        if (String(artist.bio || '').toLowerCase().includes(q)) score = 1;
        else if (
          Array.isArray(artist.styles) &&
          artist.styles.some((s) => String(s).toLowerCase().includes(q))
        ) {
          score = 1;
        }
      }

      if (score === 0) return null;
      return { artist, score };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        b.score - a.score || a.artist.displayName.localeCompare(b.artist.displayName)
    );

  return ranked.slice(0, limit).map((row) => row.artist);
}

export function clearArtistSearchIndexCache() {
  cachedIndex = null;
  cachePromise = null;
  cachedVersion = 0;
}
